import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import KeyIcon from "@mui/icons-material/Key";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
    deleteSeamApiKey,
    deleteSmartLockDoor,
    deleteSmartLockBlockedUser,
    deleteSmartLockOverride,
    deleteSmartLockPolicy,
    getSmartLockEvents,
    getSmartLockSettings,
    listSeamDevices,
    saveSeamApiKey,
    setSmartLockActivation,
    upsertSmartLockBlockedUser,
    upsertSmartLockDoor,
    upsertSmartLockOverride,
    upsertSmartLockPolicy,
} from "../api/smartLockApi";
import { getMembers } from "../api/membersApi";
import { getPlans } from "../api/planApi";

const weekdayOptions = [
    { value: 1, label: "Mon" },
    { value: 2, label: "Tue" },
    { value: 3, label: "Wed" },
    { value: 4, label: "Thu" },
    { value: 5, label: "Fri" },
    { value: 6, label: "Sat" },
    { value: 0, label: "Sun" },
];
const weekdayByValue = Object.fromEntries(weekdayOptions.map((d) => [d.value, d.label]));
const defaultDailyTimeWindows = {
    0: { enabled: true, start: "00:00", end: "23:59" },
    1: { enabled: true, start: "00:00", end: "23:59" },
    2: { enabled: true, start: "00:00", end: "23:59" },
    3: { enabled: true, start: "00:00", end: "23:59" },
    4: { enabled: true, start: "00:00", end: "23:59" },
    5: { enabled: true, start: "00:00", end: "23:59" },
    6: { enabled: true, start: "00:00", end: "23:59" },
};

const trainingTypeOptions = [
    "All classes",
    "WOD",
    "Weightlifting",
    "Cardio",
    "Rowing",
    "Gymnastics",
    "Open Gym",
    "Kids",
    "Basic",
    "Canoeing",
    "Hyrox",
    "Circuit",
    "Strength",
    "Hybrid",
    "Mobility",
];
const emptyPolicyForm = {
    name: "Default policy",
    isActive: true,
    activeMembershipRequired: true,
    requireClassBooking: false,
    minutesBeforeClassAllowed: "",
    allowContractClients: false,
    useDailyTimeWindows: false,
    dailyTimeWindows: { ...defaultDailyTimeWindows },
    allowAffiliateRole: true,
    allowTrainerRole: true,
    allowRegularRole: true,
    allowedWeekdays: [1, 2, 3, 4, 5, 6, 0],
    timeWindowStart: "00:00",
    timeWindowEnd: "23:59",
    allowedTrainingTypes: [],
    maxUnlocksPerDay: "",
    cooldownSeconds: "",
    maxUnlocksPerWindow: "",
    unlockWindowMinutes: "",
    autoCheckInOnUnlock: false,
    validFrom: "",
    validUntil: "",
    specialSchedules: [],
    doorIds: [],
    assignedUserIds: [],
    allowedPlanIds: [],
};

function toDateTimeLocalValue(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 16);
}

function InfoTooltip({ title }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!open) return undefined;

        const handleClickAway = () => setOpen(false);
        document.addEventListener("click", handleClickAway);
        return () => {
            document.removeEventListener("click", handleClickAway);
        };
    }, [open]);

    return (
        <Tooltip
            title={title}
            open={open}
            onClose={() => setOpen(false)}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            arrow
        >
            <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen((prev) => !prev);
                }}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        setOpen((prev) => !prev);
                    }
                }}
                aria-label="show explanation"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ml: 0.5,
                    p: 0,
                    color: "text.secondary",
                    backgroundColor: "transparent",
                    border: 0,
                    outline: "none",
                    cursor: "pointer",
                    lineHeight: 1,
                    "&:hover": {
                        backgroundColor: "transparent",
                        color: "primary.main",
                    },
                    "&:focus": {
                        outline: "none",
                    },
                }}
            >
                <HelpOutlineIcon fontSize="inherit" />
            </Box>
        </Tooltip>
    );
}

function TitleWithInfo({ children, info, variant = "subtitle1", sx = {} }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5, ...sx }}>
            <Typography variant={variant}>{children}</Typography>
            <InfoTooltip title={info} />
        </Box>
    );
}

function LabelWithInfo({ children, info }) {
    return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
            {children}
            <InfoTooltip title={info} />
        </Box>
    );
}

function FieldInfoWrapper({ info, children }) {
    return (
        <Box sx={{ position: "relative" }}>
            <Box
                sx={{
                    position: "absolute",
                    top: -8,
                    right: 8,
                    zIndex: 2,
                    display: "flex",
                    backgroundColor: "background.paper",
                    px: 0.25,
                }}
            >
                <InfoTooltip title={info} />
            </Box>
            {children}
        </Box>
    );
}

function mapPolicyToForm(policy) {
    if (!policy) return { ...emptyPolicyForm };
    return {
        name: policy.name || "Default policy",
        isActive: policy.isActive !== false,
        activeMembershipRequired: policy.activeMembershipRequired !== false,
        requireClassBooking: Boolean(policy.requireClassBooking),
        minutesBeforeClassAllowed: policy.minutesBeforeClassAllowed ?? "",
        allowContractClients: Boolean(policy.allowContractClients),
        useDailyTimeWindows: Boolean(policy.useDailyTimeWindows),
        dailyTimeWindows:
            policy.dailyTimeWindows && typeof policy.dailyTimeWindows === "object"
                ? {
                    ...defaultDailyTimeWindows,
                    ...policy.dailyTimeWindows,
                }
                : { ...defaultDailyTimeWindows },
        allowAffiliateRole: policy.allowAffiliateRole !== false,
        allowTrainerRole: policy.allowTrainerRole !== false,
        allowRegularRole: policy.allowRegularRole !== false,
        allowedWeekdays: Array.isArray(policy.allowedWeekdays) ? policy.allowedWeekdays : [1, 2, 3, 4, 5, 6, 0],
        timeWindowStart: policy.timeWindowStart || "00:00",
        timeWindowEnd: policy.timeWindowEnd || "23:59",
        allowedTrainingTypes: Array.isArray(policy.allowedTrainingTypes) ? policy.allowedTrainingTypes : [],
        maxUnlocksPerDay: policy.maxUnlocksPerDay ?? "",
        cooldownSeconds: policy.cooldownSeconds ?? "",
        maxUnlocksPerWindow: policy.maxUnlocksPerWindow ?? "",
        unlockWindowMinutes: policy.unlockWindowMinutes ?? "",
        autoCheckInOnUnlock: Boolean(policy.autoCheckInOnUnlock),
        validFrom: toDateTimeLocalValue(policy.validFrom),
        validUntil: toDateTimeLocalValue(policy.validUntil),
        specialSchedules: Array.isArray(policy.specialSchedules) ? policy.specialSchedules : [],
        doorIds: Array.isArray(policy.doorLinks) ? policy.doorLinks.map((link) => link.doorId) : [],
        assignedUserIds: Array.isArray(policy.assignedUsers)
            ? policy.assignedUsers.map((assignment) => assignment.userId)
            : [],
        allowedPlanIds: Array.isArray(policy.allowedPlanIds)
            ? policy.allowedPlanIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
            : [],
    };
}

export default function SmartLockSettings({ affiliateId }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [settings, setSettings] = useState(null);
    const [seamDevices, setSeamDevices] = useState([]);
    const [showApiKey, setShowApiKey] = useState("");
    const [workspaceId, setWorkspaceId] = useState("");
    const [connectedAccountId, setConnectedAccountId] = useState("");
    const [doorForm, setDoorForm] = useState({
        name: "",
        seamDeviceId: "",
        locationLabel: "",
    });
    const [overrideForm, setOverrideForm] = useState({
        userId: "",
        doorId: "",
        reason: "",
        validFrom: "",
        validUntil: "",
        active: true,
    });
    const [blockedUserForm, setBlockedUserForm] = useState({
        userId: "",
        reason: "",
        validFrom: "",
        validUntil: "",
        active: true,
    });
    const [policyForm, setPolicyForm] = useState({
        ...emptyPolicyForm,
    });
    const [selectedPolicyId, setSelectedPolicyId] = useState(null);
    const [memberOptions, setMemberOptions] = useState([]);
    const [planOptions, setPlanOptions] = useState([]);
    const [eventLogStatusFilter, setEventLogStatusFilter] = useState("");
    const [eventLogs, setEventLogs] = useState([]);
    const [eventLogPage, setEventLogPage] = useState(1);
    const [eventLogPagination, setEventLogPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    });
    const [activeSection, setActiveSection] = useState("setup");

    const selectedPolicy = useMemo(
        () => settings?.policies?.find((p) => p.id === selectedPolicyId) || null,
        [settings, selectedPolicyId]
    );

    const loadSettings = async () => {
        if (!affiliateId) return;
        setLoading(true);
        setError("");
        try {
            const response = await getSmartLockSettings(affiliateId);
            setSettings(response);
            if (response?.credentials) {
                setWorkspaceId(response.credentials.workspaceId || "");
                setConnectedAccountId(response.credentials.connectedAccountId || "");
            }
            const policies = response?.policies || [];
            if (policies.length === 0) {
                setSelectedPolicyId(null);
                setPolicyForm({ ...emptyPolicyForm });
            } else {
                const policyForForm = policies.find((policy) => policy.id === selectedPolicyId) || policies[0];
                setSelectedPolicyId(policyForForm.id);
                setPolicyForm(mapPolicyToForm(policyForForm));
            }
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load smart lock settings.");
        } finally {
            setLoading(false);
        }
    };

    const handlePolicySelection = (value) => {
        if (value === "new") {
            setSelectedPolicyId(null);
            setPolicyForm({ ...emptyPolicyForm });
            return;
        }

        const policyId = Number(value);
        const policy = settings?.policies?.find((p) => p.id === policyId);
        if (!policy) return;
        setSelectedPolicyId(policy.id);
        setPolicyForm(mapPolicyToForm(policy));
    };

    useEffect(() => {
        loadSettings();
    }, [affiliateId]);

    useEffect(() => {
        const loadMembers = async () => {
            if (!affiliateId) return;
            try {
                const members = await getMembers(affiliateId);
                const normalized = Array.isArray(members)
                    ? members
                        .filter((member) => member?.user?.id)
                        .map((member) => ({
                            id: member.user.id,
                            fullName: member.user.fullName || "Unnamed",
                            email: member.user.email || "",
                        }))
                    : [];
                setMemberOptions(normalized);
            } catch (error) {
                console.error("Error loading members for smart lock policy:", error);
                setMemberOptions([]);
            }
        };

        loadMembers();
    }, [affiliateId]);

    useEffect(() => {
        const loadPlans = async () => {
            try {
                const plans = await getPlans();
                const normalized = Array.isArray(plans)
                    ? plans
                        .filter((plan) => plan?.id)
                        .map((plan) => ({
                            id: Number(plan.id),
                            name: plan.name || `Plan ${plan.id}`,
                            active: plan.active !== false,
                        }))
                    : [];
                setPlanOptions(normalized);
            } catch (error) {
                console.error("Error loading plans for smart lock policy:", error);
                setPlanOptions([]);
            }
        };

        loadPlans();
    }, []);

    const handleSaveApiKey = async () => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await saveSeamApiKey(affiliateId, showApiKey, workspaceId, connectedAccountId);
            setShowApiKey("");
            setSuccess("Seam API key saved successfully.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to save Seam API key.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteApiKey = async () => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await deleteSeamApiKey(affiliateId);
            setSuccess("Seam API key deleted.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to delete Seam API key.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActivation = async (enabled) => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await setSmartLockActivation(affiliateId, enabled);
            setSuccess(enabled ? "Smart lock enabled for this affiliate." : "Smart lock disabled for this affiliate.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to update activation.");
        } finally {
            setLoading(false);
        }
    };

    const handleLoadSeamDevices = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await listSeamDevices(affiliateId);
            setSeamDevices(response.devices || []);
            setSuccess("Devices loaded from Seam.");
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load Seam devices.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDoor = async () => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await upsertSmartLockDoor(affiliateId, doorForm);
            setDoorForm({
                name: "",
                seamDeviceId: "",
                locationLabel: "",
            });
            setSuccess("Door saved.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to save door.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDoor = async (doorId) => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await deleteSmartLockDoor(affiliateId, doorId);
            setSuccess("Door deleted.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to delete door.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveOverride = async () => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await upsertSmartLockOverride(affiliateId, {
                ...overrideForm,
                userId: Number(overrideForm.userId),
                doorId: overrideForm.doorId ? Number(overrideForm.doorId) : null,
            });
            setOverrideForm({
                userId: "",
                doorId: "",
                reason: "",
                validFrom: "",
                validUntil: "",
                active: true,
            });
            setSuccess("Manual smart lock access saved.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to save manual access.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOverride = async (overrideId) => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await deleteSmartLockOverride(affiliateId, overrideId);
            setSuccess("Manual access deleted.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to delete manual access.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveBlockedUser = async () => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await upsertSmartLockBlockedUser(affiliateId, {
                ...blockedUserForm,
                userId: Number(blockedUserForm.userId),
            });
            setBlockedUserForm({
                userId: "",
                reason: "",
                validFrom: "",
                validUntil: "",
                active: true,
            });
            setSuccess("Blocked user saved.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to save blocked user.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteBlockedUser = async (blockId) => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await deleteSmartLockBlockedUser(affiliateId, blockId);
            setSuccess("Blocked user deleted.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to delete blocked user.");
        } finally {
            setLoading(false);
        }
    };

    const handleSavePolicy = async () => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await upsertSmartLockPolicy(affiliateId, {
                ...(selectedPolicyId ? { id: selectedPolicyId } : {}),
                ...policyForm,
                minutesBeforeClassAllowed:
                    policyForm.requireClassBooking && policyForm.minutesBeforeClassAllowed !== ""
                        ? Number(policyForm.minutesBeforeClassAllowed)
                        : null,
                allowContractClients: Boolean(policyForm.allowContractClients),
                dailyTimeWindows: policyForm.useDailyTimeWindows ? policyForm.dailyTimeWindows : null,
                maxUnlocksPerDay: policyForm.maxUnlocksPerDay ? Number(policyForm.maxUnlocksPerDay) : null,
                cooldownSeconds: policyForm.cooldownSeconds ? Number(policyForm.cooldownSeconds) : null,
                maxUnlocksPerWindow: policyForm.maxUnlocksPerWindow ? Number(policyForm.maxUnlocksPerWindow) : null,
                unlockWindowMinutes: policyForm.unlockWindowMinutes ? Number(policyForm.unlockWindowMinutes) : null,
                autoCheckInOnUnlock: Boolean(policyForm.autoCheckInOnUnlock),
                validFrom: policyForm.validFrom || null,
                validUntil: policyForm.validUntil || null,
                specialSchedules: policyForm.specialSchedules || [],
                assignedUserIds: policyForm.assignedUserIds || [],
                allowedPlanIds: policyForm.allowedPlanIds || [],
            });
            setSuccess("Smart lock policy saved.");
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to save policy.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePolicy = async () => {
        if (!selectedPolicyId) return;
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await deleteSmartLockPolicy(affiliateId, selectedPolicyId);
            setSuccess("Policy deleted.");
            setSelectedPolicyId(null);
            setPolicyForm({ ...emptyPolicyForm });
            await loadSettings();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to delete policy.");
        } finally {
            setLoading(false);
        }
    };

    const loadEventLogs = async (page = eventLogPage, status = eventLogStatusFilter) => {
        if (!affiliateId) return;
        try {
            const response = await getSmartLockEvents(affiliateId, {
                page,
                status: status || undefined,
            });
            setEventLogs(Array.isArray(response?.events) ? response.events : []);
            setEventLogPagination(response?.pagination || {
                page: 1,
                limit: 50,
                total: 0,
                totalPages: 1,
                hasNextPage: false,
                hasPreviousPage: false,
            });
        } catch (error) {
            console.error("Error loading smart lock events:", error);
        }
    };

    useEffect(() => {
        loadEventLogs();
    }, [affiliateId, eventLogStatusFilter, eventLogPage]);

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5">Smart Lock Settings (Seam)</Typography>
                <Typography variant="body2" color="text.secondary">
                    Configure provider setup, access policies, exceptions, and audit logs from separate sections.
                </Typography>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Card sx={{ mb: 2 }}>
                <Tabs
                    value={activeSection}
                    onChange={(event, value) => setActiveSection(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ px: 1 }}
                >
                    <Tab value="setup" label={`Setup (${settings?.doors?.length || 0} doors)`} />
                    <Tab value="policies" label={`Policies (${settings?.policies?.length || 0})`} />
                    <Tab value="exceptions" label={`Overrides & Blocks (${(settings?.overrides?.length || 0) + (settings?.blockedUsers?.length || 0)})`} />
                    <Tab value="logs" label="Access Logs" />
                </Tabs>
            </Card>

            {activeSection === "setup" && (
                <>
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <TitleWithInfo
                        variant="h6"
                        sx={{ mb: 1 }}
                        info="Controls whether smart lock access is enabled for this club. Users can only see and use unlock buttons when this is enabled."
                    >
                        Activation
                    </TitleWithInfo>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(settings?.isSmartLockEnabled)}
                                onChange={(event) => handleToggleActivation(event.target.checked)}
                                disabled={loading}
                            />
                        }
                        label={settings?.isSmartLockEnabled ? "Smart lock access is enabled" : "Smart lock access is disabled"}
                    />
                </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <TitleWithInfo
                        variant="h6"
                        sx={{ mb: 1 }}
                        info="The club's Seam API key is saved encrypted in the backend. Irontrack uses it to list devices and send unlock commands."
                    >
                        Seam API Key (encrypted in backend)
                    </TitleWithInfo>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="password"
                                label="Seam API Key"
                                value={showApiKey}
                                onChange={(event) => setShowApiKey(event.target.value)}
                                placeholder="Paste Seam key from club account"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Workspace ID (optional)"
                                value={workspaceId}
                                onChange={(event) => setWorkspaceId(event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Connected Account ID (optional)"
                                value={connectedAccountId}
                                onChange={(event) => setConnectedAccountId(event.target.value)}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<KeyIcon />}
                            onClick={handleSaveApiKey}
                            disabled={loading || !showApiKey.trim()}
                        >
                            Save Key
                        </Button>
                        {settings?.hasApiKey && (
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteIcon />}
                                onClick={handleDeleteApiKey}
                                disabled={loading}
                            >
                                Delete Key
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<LockOpenIcon />}
                            onClick={handleLoadSeamDevices}
                            disabled={loading || !settings?.hasApiKey}
                        >
                            Load Seam Devices
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <TitleWithInfo
                        variant="h6"
                        sx={{ mb: 1 }}
                        info="Doors are Irontrack records linked to Seam devices. Policies are applied to these doors."
                    >
                        Doors
                    </TitleWithInfo>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Seam Device</InputLabel>
                                <Select
                                    value={doorForm.seamDeviceId}
                                    label="Seam Device"
                                    onChange={(event) => setDoorForm((prev) => ({ ...prev, seamDeviceId: event.target.value }))}
                                >
                                    {seamDevices.map((device) => (
                                        <MenuItem key={device.id} value={device.id}>
                                            {device.displayName} ({device.id})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Door Name"
                                value={doorForm.name}
                                onChange={(event) => setDoorForm((prev) => ({ ...prev, name: event.target.value }))}
                                placeholder="Main Entrance"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Location Label"
                                value={doorForm.locationLabel}
                                onChange={(event) => setDoorForm((prev) => ({ ...prev, locationLabel: event.target.value }))}
                                placeholder="Ground floor"
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveDoor}
                            disabled={loading || !doorForm.name.trim() || !doorForm.seamDeviceId}
                        >
                            Save Door
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <TitleWithInfo
                        sx={{ mb: 1 }}
                        info="These doors are already configured for this club. Removing a door also removes access through that Irontrack door record."
                    >
                        Configured doors
                    </TitleWithInfo>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {settings?.doors?.map((door) => (
                            <Chip
                                key={door.id}
                                label={`${door.name}${door.locationLabel ? ` - ${door.locationLabel}` : ""}`}
                                onDelete={() => handleDeleteDoor(door.id)}
                                deleteIcon={<DeleteIcon />}
                                color="primary"
                                variant="outlined"
                            />
                        ))}
                        {!settings?.doors?.length && (
                            <Typography variant="body2" color="text.secondary">No doors configured yet.</Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
                </>
            )}

            {activeSection === "exceptions" && (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <TitleWithInfo
                        variant="h6"
                        sx={{ mb: 1 }}
                        info="Use this section for exceptions: temporary manual access or blocking a user from smart lock access."
                    >
                        Manual Access and Blocked Users
                    </TitleWithInfo>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Manual access lets a selected user open the door even if no policy matches. Blocked users are denied before any manual access or policy is checked.
                    </Typography>

                    <TitleWithInfo
                        variant="subtitle2"
                        sx={{ mb: 1 }}
                        info="Manual access lets a selected user open a selected door, or all doors, even if normal policies do not match. Blocked users still stay blocked."
                    >
                        Manual access override
                    </TitleWithInfo>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={overrideForm.userId}
                                    label="User"
                                    onChange={(event) => setOverrideForm((prev) => ({ ...prev, userId: event.target.value }))}
                                >
                                    {memberOptions.map((member) => (
                                        <MenuItem key={member.id} value={member.id}>
                                            {member.fullName}{member.email ? ` (${member.email})` : ""}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Door</InputLabel>
                                <Select
                                    value={overrideForm.doorId}
                                    label="Door"
                                    onChange={(event) => setOverrideForm((prev) => ({ ...prev, doorId: event.target.value }))}
                                >
                                    <MenuItem value="">All doors</MenuItem>
                                    {(settings?.doors || []).map((door) => (
                                        <MenuItem key={door.id} value={door.id}>{door.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="datetime-local"
                                label="Valid from"
                                value={overrideForm.validFrom}
                                onChange={(event) => setOverrideForm((prev) => ({ ...prev, validFrom: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="datetime-local"
                                label="Valid until"
                                value={overrideForm.validUntil}
                                onChange={(event) => setOverrideForm((prev) => ({ ...prev, validUntil: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField
                                fullWidth
                                label="Reason"
                                value={overrideForm.reason}
                                onChange={(event) => setOverrideForm((prev) => ({ ...prev, reason: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleSaveOverride}
                                disabled={loading || !overrideForm.userId}
                            >
                                Save Manual Access
                            </Button>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {(settings?.overrides || []).map((override) => (
                            <Chip
                                key={override.id}
                                label={`${override.user?.fullName || override.user?.email || `User ${override.userId}`} - ${override.door?.name || "All doors"}`}
                                color={override.active ? "success" : "default"}
                                variant="outlined"
                                onDelete={() => handleDeleteOverride(override.id)}
                                deleteIcon={<DeleteIcon />}
                            />
                        ))}
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <TitleWithInfo
                        variant="subtitle2"
                        sx={{ mb: 1 }}
                        info="A blocked user is denied before manual access or policy rules are checked. Use this for bans, security issues, or temporary restrictions."
                    >
                        Blocked user
                    </TitleWithInfo>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={blockedUserForm.userId}
                                    label="User"
                                    onChange={(event) => setBlockedUserForm((prev) => ({ ...prev, userId: event.target.value }))}
                                >
                                    {memberOptions.map((member) => (
                                        <MenuItem key={member.id} value={member.id}>
                                            {member.fullName}{member.email ? ` (${member.email})` : ""}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="datetime-local"
                                label="Valid from"
                                value={blockedUserForm.validFrom}
                                onChange={(event) => setBlockedUserForm((prev) => ({ ...prev, validFrom: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="datetime-local"
                                label="Valid until"
                                value={blockedUserForm.validUntil}
                                onChange={(event) => setBlockedUserForm((prev) => ({ ...prev, validUntil: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                onClick={handleSaveBlockedUser}
                                disabled={loading || !blockedUserForm.userId}
                            >
                                Block User
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Block reason"
                                value={blockedUserForm.reason}
                                onChange={(event) => setBlockedUserForm((prev) => ({ ...prev, reason: event.target.value }))}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {(settings?.blockedUsers || []).map((block) => (
                            <Chip
                                key={block.id}
                                label={block.user?.fullName || block.user?.email || `User ${block.userId}`}
                                color={block.active ? "error" : "default"}
                                variant="outlined"
                                onDelete={() => handleDeleteBlockedUser(block.id)}
                                deleteIcon={<DeleteIcon />}
                            />
                        ))}
                    </Box>
                </CardContent>
            </Card>
            )}

            {activeSection === "policies" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Card>
                <CardContent>
                    <TitleWithInfo
                        variant="h6"
                        sx={{ mb: 1 }}
                        info="A policy is a reusable set of rules. A door can be linked to multiple policies, and access is granted when one policy matches."
                    >
                        Policies
                    </TitleWithInfo>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose an existing policy or create a new one. Each policy can be linked to one or more doors.
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={8}>
                            <FormControl fullWidth>
                                <InputLabel>Policy</InputLabel>
                                <Select
                                    value={selectedPolicyId ?? "new"}
                                    label="Policy"
                                    onChange={(event) => handlePolicySelection(event.target.value)}
                                >
                                    <MenuItem value="new">Create new policy</MenuItem>
                                    {(settings?.policies || []).map((policy) => (
                                        <MenuItem key={policy.id} value={policy.id}>
                                            {policy.name} {policy.isActive ? "(active)" : "(inactive)"}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => handlePolicySelection("new")}
                            >
                                New Policy
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <TitleWithInfo
                        sx={{ mb: 0.5 }}
                        info="Basic rules define whether the policy is active, when it is valid, whether membership or class booking is required, and which roles can use it."
                    >
                        Basic Rules
                    </TitleWithInfo>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Set policy name, active dates, membership rules, class booking behavior, and roles.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Policy Name"
                                value={policyForm.name}
                                onChange={(event) => setPolicyForm((prev) => ({ ...prev, name: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FieldInfoWrapper info="Optional start date/time for this policy. Before this moment the policy cannot grant access.">
                                <TextField
                                    fullWidth
                                    label="Valid From"
                                    type="datetime-local"
                                    value={policyForm.validFrom}
                                    onChange={(event) => setPolicyForm((prev) => ({ ...prev, validFrom: event.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Optional"
                                />
                            </FieldInfoWrapper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FieldInfoWrapper info="Optional end date/time for this policy. After this moment the policy no longer grants access.">
                                <TextField
                                    fullWidth
                                    label="Valid Until"
                                    type="datetime-local"
                                    value={policyForm.validUntil}
                                    onChange={(event) => setPolicyForm((prev) => ({ ...prev, validUntil: event.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Optional"
                                />
                            </FieldInfoWrapper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Time Window Start"
                                type="time"
                                value={policyForm.timeWindowStart}
                                onChange={(event) => setPolicyForm((prev) => ({ ...prev, timeWindowStart: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Time Window End"
                                type="time"
                                value={policyForm.timeWindowEnd}
                                onChange={(event) => setPolicyForm((prev) => ({ ...prev, timeWindowEnd: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <FormGroup row sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={<Checkbox checked={policyForm.isActive} onChange={(e) => setPolicyForm((prev) => ({ ...prev, isActive: e.target.checked }))} />}
                            label="Policy active"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={policyForm.activeMembershipRequired} onChange={(e) => setPolicyForm((prev) => ({ ...prev, activeMembershipRequired: e.target.checked }))} />}
                            label="Require active membership"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={policyForm.requireClassBooking} onChange={(e) => setPolicyForm((prev) => ({ ...prev, requireClassBooking: e.target.checked }))} />}
                            label={
                                <LabelWithInfo info="When enabled, the user must be booked into the selected class before the policy can allow the door unlock.">
                                    Require class booking
                                </LabelWithInfo>
                            }
                        />
                        <FormControlLabel
                            control={<Checkbox checked={policyForm.autoCheckInOnUnlock} onChange={(e) => setPolicyForm((prev) => ({ ...prev, autoCheckInOnUnlock: e.target.checked }))} />}
                            label={
                                <LabelWithInfo info="When enabled, a successful class-related door unlock also marks the user as checked in for that class.">
                                    Auto check-in on unlock
                                </LabelWithInfo>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={policyForm.allowContractClients}
                                    onChange={(e) =>
                                        setPolicyForm((prev) => ({
                                            ...prev,
                                            allowContractClients: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label={
                                <LabelWithInfo info="Allows users with a valid contract-based plan. If no allowed plans are selected, only contract clients can match this entitlement rule.">
                                    Allow contract clients
                                </LabelWithInfo>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={policyForm.useDailyTimeWindows}
                                    onChange={(e) =>
                                        setPolicyForm((prev) => ({
                                            ...prev,
                                            useDailyTimeWindows: e.target.checked,
                                            dailyTimeWindows: e.target.checked
                                                ? (prev.dailyTimeWindows || { ...defaultDailyTimeWindows })
                                                : prev.dailyTimeWindows,
                                        }))
                                    }
                                />
                            }
                            label={
                                <LabelWithInfo info="Enable this when each weekday needs its own start/end time instead of one shared time window.">
                                    Use separate daily time windows
                                </LabelWithInfo>
                            }
                        />
                    </FormGroup>

                    {policyForm.requireClassBooking && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} md={6}>
                                <FieldInfoWrapper info="Example: 15 means the door can be opened starting 15 minutes before the class begins.">
                                    <TextField
                                        fullWidth
                                        label="Minutes before class entry is allowed"
                                        type="number"
                                        value={policyForm.minutesBeforeClassAllowed}
                                        onChange={(event) =>
                                            setPolicyForm((prev) => ({
                                                ...prev,
                                                minutesBeforeClassAllowed: event.target.value,
                                            }))
                                        }
                                        inputProps={{ min: 0 }}
                                        helperText="Example: 15 means users can open door up to 15 minutes before class."
                                    />
                                </FieldInfoWrapper>
                            </Grid>
                        </Grid>
                    )}

                    <TitleWithInfo
                        variant="subtitle2"
                        sx={{ mt: 2 }}
                        info="Choose which account roles can match this policy. This is checked together with the rest of the policy rules."
                    >
                        Allowed roles
                    </TitleWithInfo>
                    <FormGroup row>
                        <FormControlLabel
                            control={<Checkbox checked={policyForm.allowAffiliateRole} onChange={(e) => setPolicyForm((prev) => ({ ...prev, allowAffiliateRole: e.target.checked }))} />}
                            label="Affiliate"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={policyForm.allowTrainerRole} onChange={(e) => setPolicyForm((prev) => ({ ...prev, allowTrainerRole: e.target.checked }))} />}
                            label="Trainer"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={policyForm.allowRegularRole} onChange={(e) => setPolicyForm((prev) => ({ ...prev, allowRegularRole: e.target.checked }))} />}
                            label="Regular"
                        />
                    </FormGroup>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <TitleWithInfo
                        sx={{ mb: 0.5 }}
                        info="Access scope narrows who or what this policy applies to: specific users, plans, class types, and doors."
                    >
                        Access Scope
                    </TitleWithInfo>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Limit the policy to selected users, plans, training types, or covered doors.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <TitleWithInfo
                            variant="subtitle2"
                            sx={{ mb: 1 }}
                            info="If users are selected here, only those users can match this policy. Leave empty to allow all users who satisfy the other conditions."
                        >
                            Assign policy to specific users (optional)
                        </TitleWithInfo>
                        <Autocomplete
                            multiple
                            options={memberOptions}
                            value={memberOptions.filter((member) => (policyForm.assignedUserIds || []).includes(member.id))}
                            onChange={(event, newValue) =>
                                setPolicyForm((prev) => ({
                                    ...prev,
                                    assignedUserIds: newValue.map((member) => member.id),
                                }))
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => `${option.fullName}${option.email ? ` (${option.email})` : ""}`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Allowed users for this policy"
                                    placeholder="Select members"
                                    helperText="Leave empty to allow all users matching the policy conditions."
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <TitleWithInfo
                            variant="subtitle2"
                            sx={{ mb: 1 }}
                            info="If plans are selected, the user must have one of those plans with sessions left. If empty, plan restriction is not applied unless other membership rules require it."
                        >
                            Allowed plans (optional)
                        </TitleWithInfo>
                        <FormControl fullWidth>
                            <InputLabel>Plans that can use this policy</InputLabel>
                            <Select
                                multiple
                                value={policyForm.allowedPlanIds || []}
                                label="Plans that can use this policy"
                                onChange={(event) =>
                                    setPolicyForm((prev) => ({
                                        ...prev,
                                        allowedPlanIds: event.target.value.map((id) => Number(id)),
                                    }))
                                }
                                renderValue={(selected) =>
                                    selected
                                        .map((id) => planOptions.find((plan) => plan.id === id)?.name || `Plan ${id}`)
                                        .join(", ")
                                }
                            >
                                {planOptions.map((plan) => (
                                    <MenuItem key={plan.id} value={plan.id}>
                                        {plan.name} {plan.active ? "(active)" : "(inactive)"}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary">
                            Leave empty to allow all plans.
                        </Typography>
                    </Box>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Allowed Training Types</InputLabel>
                                <Select
                                    multiple
                                    value={policyForm.allowedTrainingTypes}
                                    label="Allowed Training Types"
                                    onChange={(event) =>
                                        setPolicyForm((prev) => ({
                                            ...prev,
                                            allowedTrainingTypes: event.target.value,
                                        }))
                                    }
                                >
                                    {trainingTypeOptions.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormControl fullWidth>
                                <InputLabel>Policy Doors</InputLabel>
                                <Select
                                    multiple
                                    value={policyForm.doorIds}
                                    label="Policy Doors"
                                    onChange={(event) => setPolicyForm((prev) => ({ ...prev, doorIds: event.target.value }))}
                                >
                                    {(settings?.doors || []).map((door) => (
                                        <MenuItem key={door.id} value={door.id}>
                                            {door.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <TitleWithInfo
                        sx={{ mb: 0.5 }}
                        info="Weekly schedule controls on which days and at what times this policy can grant access."
                    >
                        Weekly Schedule
                    </TitleWithInfo>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Use either a simple weekday list with one time window, or separate time windows for every day.
                    </Typography>
                    <TitleWithInfo
                        variant="subtitle2"
                        sx={{ mt: 2 }}
                        info="When separate daily time windows are off, these weekdays share the same start and end time. When enabled, each day has its own window."
                    >
                        Allowed weekdays
                    </TitleWithInfo>
                    {!policyForm.useDailyTimeWindows ? (
                        <FormGroup row>
                            {weekdayOptions.map((day) => (
                                <FormControlLabel
                                    key={day.value}
                                    control={
                                        <Checkbox
                                            checked={policyForm.allowedWeekdays.includes(day.value)}
                                            onChange={(event) => {
                                                setPolicyForm((prev) => ({
                                                    ...prev,
                                                    allowedWeekdays: event.target.checked
                                                        ? [...prev.allowedWeekdays, day.value]
                                                        : prev.allowedWeekdays.filter((value) => value !== day.value),
                                                }));
                                            }}
                                        />
                                    }
                                    label={day.label}
                                />
                            ))}
                        </FormGroup>
                    ) : (
                        <Grid container spacing={1}>
                            {[1, 2, 3, 4, 5, 6, 0].map((dayValue) => {
                                const dayWindow = policyForm.dailyTimeWindows?.[dayValue] || defaultDailyTimeWindows[dayValue];
                                return (
                                    <Grid item xs={12} key={dayValue}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={Boolean(dayWindow.enabled)}
                                                        onChange={(event) =>
                                                            setPolicyForm((prev) => ({
                                                                ...prev,
                                                                dailyTimeWindows: {
                                                                    ...prev.dailyTimeWindows,
                                                                    [dayValue]: {
                                                                        ...prev.dailyTimeWindows?.[dayValue],
                                                                        enabled: event.target.checked,
                                                                    },
                                                                },
                                                            }))
                                                        }
                                                    />
                                                }
                                                label={weekdayByValue[dayValue]}
                                                sx={{ minWidth: 120 }}
                                            />
                                            <TextField
                                                size="small"
                                                type="time"
                                                label="Start"
                                                InputLabelProps={{ shrink: true }}
                                                value={dayWindow.start || "00:00"}
                                                disabled={!dayWindow.enabled}
                                                onChange={(event) =>
                                                    setPolicyForm((prev) => ({
                                                        ...prev,
                                                        dailyTimeWindows: {
                                                            ...prev.dailyTimeWindows,
                                                            [dayValue]: {
                                                                ...prev.dailyTimeWindows?.[dayValue],
                                                                start: event.target.value,
                                                            },
                                                        },
                                                    }))
                                                }
                                            />
                                            <TextField
                                                size="small"
                                                type="time"
                                                label="End"
                                                InputLabelProps={{ shrink: true }}
                                                value={dayWindow.end || "23:59"}
                                                disabled={!dayWindow.enabled}
                                                onChange={(event) =>
                                                    setPolicyForm((prev) => ({
                                                        ...prev,
                                                        dailyTimeWindows: {
                                                            ...prev.dailyTimeWindows,
                                                            [dayValue]: {
                                                                ...prev.dailyTimeWindows?.[dayValue],
                                                                end: event.target.value,
                                                            },
                                                        },
                                                    }))
                                                }
                                            />
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <TitleWithInfo
                        sx={{ mb: 0.5 }}
                        info="Limits reduce misuse by capping how often a user can unlock a door."
                    >
                        Limits
                    </TitleWithInfo>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Prevent repeated unlock spam with daily, cooldown, and rolling-window limits.
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                            <FieldInfoWrapper info="Maximum successful unlocks this user can make per day under this policy. Leave empty for no daily cap.">
                                <TextField
                                    fullWidth
                                    label="Max unlocks per day (optional)"
                                    type="number"
                                    value={policyForm.maxUnlocksPerDay}
                                    onChange={(event) => setPolicyForm((prev) => ({ ...prev, maxUnlocksPerDay: event.target.value }))}
                                />
                            </FieldInfoWrapper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FieldInfoWrapper info="Minimum wait time before the same user can unlock the same door again. Useful to prevent repeated tapping.">
                                <TextField
                                    fullWidth
                                    label="Cooldown seconds (optional)"
                                    type="number"
                                    value={policyForm.cooldownSeconds}
                                    onChange={(event) => setPolicyForm((prev) => ({ ...prev, cooldownSeconds: event.target.value }))}
                                    helperText="Minimum seconds between same user's unlocks on this door."
                                />
                            </FieldInfoWrapper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FieldInfoWrapper info="Maximum unlocks allowed during the moving time window below. Example: 3 unlocks in the last 60 minutes.">
                                <TextField
                                    fullWidth
                                    label="Max unlocks per rolling window"
                                    type="number"
                                    value={policyForm.maxUnlocksPerWindow}
                                    onChange={(event) => setPolicyForm((prev) => ({ ...prev, maxUnlocksPerWindow: event.target.value }))}
                                />
                            </FieldInfoWrapper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FieldInfoWrapper info="How far back the system looks when counting rolling-window unlocks. Example: 60 means the last 60 minutes from the current moment.">
                                <TextField
                                    fullWidth
                                    label="Rolling window minutes"
                                    type="number"
                                    value={policyForm.unlockWindowMinutes}
                                    onChange={(event) => setPolicyForm((prev) => ({ ...prev, unlockWindowMinutes: event.target.value }))}
                                />
                            </FieldInfoWrapper>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <TitleWithInfo
                        sx={{ mb: 0.5 }}
                        info="Special dates override the normal weekly schedule for a specific date, for example holidays, closed days, or one-off extended hours."
                    >
                        Special Dates
                    </TitleWithInfo>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add holidays, closure days, or one-off custom time windows.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <TitleWithInfo
                            variant="subtitle2"
                            sx={{ mb: 1 }}
                            info="Add a date, choose whether the door is closed that day, or define a custom time window just for that date."
                        >
                            Holiday / special schedule
                        </TitleWithInfo>
                        <Typography variant="caption" color="text.secondary">
                            If a date matches here, it overrides the normal weekday/time window for that date.
                        </Typography>
                        {(policyForm.specialSchedules || []).map((schedule, index) => (
                            <Grid container spacing={1} sx={{ mt: 1 }} key={`${schedule.date || "special"}-${index}`}>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label="Date"
                                        value={schedule.date || ""}
                                        onChange={(event) =>
                                            setPolicyForm((prev) => ({
                                                ...prev,
                                                specialSchedules: prev.specialSchedules.map((item, itemIndex) =>
                                                    itemIndex === index ? { ...item, date: event.target.value } : item
                                                ),
                                            }))
                                        }
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="time"
                                        label="Start"
                                        value={schedule.start || "00:00"}
                                        disabled={Boolean(schedule.closed)}
                                        onChange={(event) =>
                                            setPolicyForm((prev) => ({
                                                ...prev,
                                                specialSchedules: prev.specialSchedules.map((item, itemIndex) =>
                                                    itemIndex === index ? { ...item, start: event.target.value } : item
                                                ),
                                            }))
                                        }
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="time"
                                        label="End"
                                        value={schedule.end || "23:59"}
                                        disabled={Boolean(schedule.closed)}
                                        onChange={(event) =>
                                            setPolicyForm((prev) => ({
                                                ...prev,
                                                specialSchedules: prev.specialSchedules.map((item, itemIndex) =>
                                                    itemIndex === index ? { ...item, end: event.target.value } : item
                                                ),
                                            }))
                                        }
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(schedule.closed)}
                                                onChange={(event) =>
                                                    setPolicyForm((prev) => ({
                                                        ...prev,
                                                        specialSchedules: prev.specialSchedules.map((item, itemIndex) =>
                                                            itemIndex === index ? { ...item, closed: event.target.checked } : item
                                                        ),
                                                    }))
                                                }
                                            />
                                        }
                                        label="Closed"
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Note"
                                        value={schedule.note || ""}
                                        onChange={(event) =>
                                            setPolicyForm((prev) => ({
                                                ...prev,
                                                specialSchedules: prev.specialSchedules.map((item, itemIndex) =>
                                                    itemIndex === index ? { ...item, note: event.target.value } : item
                                                ),
                                            }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={1}>
                                    <IconButton
                                        color="error"
                                        onClick={() =>
                                            setPolicyForm((prev) => ({
                                                ...prev,
                                                specialSchedules: prev.specialSchedules.filter((item, itemIndex) => itemIndex !== index),
                                            }))
                                        }
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        ))}
                        <Button
                            sx={{ mt: 1 }}
                            size="small"
                            variant="outlined"
                            onClick={() =>
                                setPolicyForm((prev) => ({
                                    ...prev,
                                    specialSchedules: [
                                        ...(prev.specialSchedules || []),
                                        { date: "", start: "00:00", end: "23:59", closed: false, note: "" },
                                    ],
                                }))
                            }
                        >
                            Add Special Date
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSavePolicy}
                            disabled={loading || !policyForm.name.trim() || policyForm.doorIds.length === 0}
                        >
                            Save Conditions
                        </Button>
                        {selectedPolicyId && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleDeletePolicy}
                                disabled={loading}
                            >
                                Delete Policy
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
            </Box>
            )}

            {activeSection === "logs" && (
            <Card sx={{ mt: 2 }}>
                <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <TitleWithInfo
                            variant="h6"
                            sx={{ mb: 0 }}
                            info="Audit log of granted and denied smart lock attempts. Logs are loaded 50 at a time from the backend."
                        >
                            Access Event Logs
                        </TitleWithInfo>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={eventLogStatusFilter}
                                    label="Status"
                                    onChange={(event) => {
                                        setEventLogStatusFilter(event.target.value);
                                        setEventLogPage(1);
                                    }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="granted">Granted</MenuItem>
                                    <MenuItem value="denied">Denied</MenuItem>
                                </Select>
                            </FormControl>
                            <IconButton onClick={() => loadEventLogs(eventLogPage, eventLogStatusFilter)} aria-label="refresh-logs">
                                <RefreshIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Time</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Door</TableCell>
                                    <TableCell>Policy</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Reason</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {eventLogs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                No smart lock events yet.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    eventLogs.map((eventItem) => (
                                        <TableRow key={eventItem.id}>
                                            <TableCell>
                                                {new Date(eventItem.requestedAt).toLocaleString("et-EE", {
                                                    day: "2-digit",
                                                    month: "2-digit",
                                                    year: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                })}
                                            </TableCell>
                                            <TableCell>
                                                {eventItem.user?.fullName || eventItem.user?.email || `User ${eventItem.userId}`}
                                            </TableCell>
                                            <TableCell>{eventItem.door?.name || "-"}</TableCell>
                                            <TableCell>{eventItem.policy?.name || "-"}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={eventItem.status}
                                                    color={eventItem.status === "granted" ? "success" : "error"}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{eventItem.reason || "-"}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                            Showing page {eventLogPagination.page} of {eventLogPagination.totalPages} ({eventLogPagination.total} total events)
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={!eventLogPagination.hasPreviousPage}
                                onClick={() => setEventLogPage((prev) => Math.max(prev - 1, 1))}
                            >
                                Previous
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={!eventLogPagination.hasNextPage}
                                onClick={() => setEventLogPage((prev) => prev + 1)}
                            >
                                Next
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            )}
        </Box>
    );
}
